/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  AfterViewInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  afterEveryRender,
  NgZone,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule
} from '@angular/forms';
import { DatePicker, DatePickerStyle } from 'primeng/datepicker';
import { OverlayService } from 'primeng/api';
import { NgClass } from '@angular/common';

/** Shape of the day metadata PrimeNG passes to DatePicker.dayClass. */
export interface DayMeta {
  day: number;
  month: number;
  year: number;
  selectable: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-calendar',
  templateUrl: './m-calendar.component.html',
  styleUrls: ['./m-calendar.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MCalendarComponent),
      multi: true
    },
    DatePickerStyle
  ],
  imports: [NgClass, DatePicker, FormsModule]
})
export class MCalendarComponent
  extends DatePicker
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @ViewChild(DatePicker) pCalendar!: DatePicker;
  @Input() resolution: string | null = null;

  /**
   * Which end of a range the visible months follow when the value changes:
   * `'start'` (default) re-anchors on the range start, `'end'` keeps PrimeNG's
   * native end-anchoring, `'none'` freezes the view after the first build (the
   * consumer positions it). Only affects range mode.
   */
  @Input() anchorRangeOn: 'start' | 'end' | 'none' = 'start';

  /**
   * Single-endpoint edit mode. When on, a day click commits exactly that one
   * date via `endpointSelected` instead of mutating the range — the consumer
   * owns the value and decides what the click means. Off (default) leaves
   * native range/single selection untouched, so existing consumers are inert.
   * Which days are pickable is left to the consumer's `minDate`/`maxDate`.
   */
  @Input() editingEndpoint = false;
  @Output() endpointSelected = new EventEmitter<Date>();

  /**
   * Render a selected range as a flat pill — no ring on the endpoints. Off by
   * default (consumers keep the ringed endpoints); paired with `editEndpoint`
   * it gives the schedule calendar its ringless look.
   */
  @Input() flatRange = false;

  constructor() {
    const zone = inject(NgZone);
    const overlayService = inject(OverlayService);

    super(zone, overlayService);

    // Every grid rebuild drops the imperatively-tagged endpoints; re-tag them.
    afterEveryRender({
      write: () => this.applyStartAndEndClasses(this.value)
    });
  }

  // CVA callbacks, replaced when Angular registers the real ones.
  private onChangeCb: (value: any) => void = () => undefined;
  private onTouchedCb: () => void = () => undefined;

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.patchRangeDisabledDayClass();
    this.patchRangeAnchor();
    this.patchWeekdayLabels();
    this.patchEditEndpointSelect();
    const pCalendar = this.pCalendar;
    if (pCalendar && !this.inline && pCalendar.inputfieldViewChild) {
      this.renderer.listen(
        pCalendar.inputfieldViewChild.nativeElement,
        'click',
        () => {
          this.pCalendar.toggle();
          this.pCalendar.cd.markForCheck();
        }
      );
    }
    // Patches above install after the picker's first updateUI/createWeekDays,
    // so re-run them here — in rAF, past this CD pass, to dodge
    // ExpressionChangedAfterItHasBeenChecked. detectChanges flushes the rebuilt
    // grid before tagging cells, else the CD wipes the endpoint classes.
    requestAnimationFrame(() => {
      this.pCalendar?.updateUI();
      this.pCalendar?.createWeekDays();
      this.pCalendar?.cd.detectChanges();
      this.applyStartAndEndClasses(this.value);
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  // PrimeNG 20 applies root host class/style on the host via
  // `host: { '[class]': "cn(cx('root'), styleClass)", '[style]': "sx('root')" }`. This wrapper
  // extends DatePicker but renders its OWN inner <p-datepicker>, so that metadata is inherited onto
  // <m-calendar> too — duplicating styling. Neutralize on the wrapper host; inner is unaffected.
  override cx(): string {
    return '';
  }

  override sx(): { [klass: string]: unknown } | undefined {
    return undefined;
  }

  // ControlValueAccessor implementation
  override writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  override registerOnChange(fn: (value: any) => void): void {
    this.onChangeCb = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this.onTouchedCb = fn;
  }

  // Handle inner p-calendar model changes and propagate to parent
  onInnerModelChange = (val: any) => {
    this.value = val;
    this.onChangeCb(val);
    this.onTouchedCb();
  };

  onHoverDate(event: MouseEvent): void {
    if (this.selectionMode === 'range' && this.resolution === 'week') {
      this.clearPreviousPreviewHighlight();
      const target = (event.target as HTMLElement).closest('tr');
      if (target) {
        this.renderer.addClass(target, 'preview-range');
      }
    }
  }

  clearPreviousPreviewHighlight(): void {
    const highlightedElements =
      this.el.nativeElement.querySelectorAll('.preview-range');
    highlightedElements.forEach((el: HTMLElement) => {
      this.renderer.removeClass(el, 'preview-range');
    });
  }

  applyStartAndEndClasses(dates: Date[]): void {
    if (!Array.isArray(dates)) return;

    this.clearSelections();

    // The selected-start / selected-end blue "pill" is a range-only treatment.
    // In multiple mode the array holds independent selections (not a start/end
    // pair), so tagging the first two would wrongly give them a range endpoint
    // background — each multiple-mode day should show the red ring only.
    if (this.selectionMode !== 'range') return;

    const [startDate, endDate] = dates;

    if (startDate) {
      const startKey = this.formatDateKey(startDate);
      const startElement = this.el.nativeElement
        .querySelector(`[data-date="${startKey}"]:not(.p-disabled)`)
        ?.closest('td');
      if (startElement) {
        this.renderer.addClass(startElement, 'selected-start');
      }
    }
    if (endDate) {
      const endKey = this.formatDateKey(endDate);
      const endElement = this.el.nativeElement
        .querySelector(`[data-date="${endKey}"]:not(.p-disabled)`)
        ?.closest('td');
      if (endElement) {
        this.renderer.addClass(endElement, 'selected-end');
      }
    }
  }

  /**
   * Non-selectable days inside a range (e.g. a running test's elapsed days) are
   * disabled cells with the highlight withheld by default. Re-add the range
   * classes and drop `p-disabled` so the range reads as continuous; the
   * `m-calendar-day-range-disabled` tag gives them the muted, inert scss look.
   */
  private patchRangeDisabledDayClass(): void {
    const picker = this.pCalendar;
    if (!picker) return;
    const dayClass = picker.dayClass.bind(picker);
    picker.dayClass = (date: DayMeta) => {
      const classes = dayClass(date);
      if (
        !date.selectable &&
        picker.isRangeSelection() &&
        picker.isSelected(date)
      ) {
        const [start, end] = (picker.value ?? []) as (Date | null)[];
        const isEdge = this.isSameDay(start, date) || this.isSameDay(end, date);
        classes[
          isEdge
            ? 'p-datepicker-day-selected'
            : 'p-datepicker-day-selected-range'
        ] = true;
        // Only drop the per-day disabled flag, never the whole-calendar one.
        if (!picker.$disabled()) {
          classes['p-disabled'] = false;
        }
        classes['m-calendar-day-range-disabled'] = true;
      }
      return classes;
    };
  }

  /**
   * PrimeNG anchors the visible months on the range END (`updateUI` uses the
   * last value), pushing the start off-screen when a range is set
   * programmatically. Wrap `updateUI` to honour `anchorRangeOn`:
   *  - `'end'`  — leave PrimeNG's native end-anchoring.
   *  - `'start'`— re-anchor on the range start, rebuilding the grid only when
   *    the start lands in a different month.
   *  - `'none'` — build the grid once, then freeze the view so later value
   *    writes never move it (the consumer owns positioning).
   * Single and multiple modes are untouched.
   */
  private patchRangeAnchor(): void {
    const picker = this.pCalendar;
    if (!picker) return;
    const original = picker.updateUI.bind(picker);
    picker.updateUI = () => {
      if (this.anchorRangeOn === 'none') {
        if (!picker.months?.length) original();
        return;
      }
      original();
      if (this.anchorRangeOn !== 'start') return;
      if (!picker.isRangeSelection() || !Array.isArray(picker.value)) return;
      const start = picker.value[0] as Date | null;
      if (start && picker.isValidDate(start)) {
        const month = start.getMonth();
        const year = start.getFullYear();
        if (picker.currentMonth === month && picker.currentYear === year)
          return;
        picker.currentMonth = month;
        picker.currentYear = year;
        picker.createMonths(month, year);
      }
    };
  }

  /**
   * Route day clicks through `editingEndpoint` mode. PrimeNG's `onDateSelect`
   * is the sole funnel for a day-cell click; while an endpoint is being edited
   * we emit that single date and skip the base range mutation + `onSelect`, so
   * one click commits and nothing desyncs. Unselectable clicks (gated by
   * `minDate`/`maxDate`) are swallowed. Off falls straight through to base.
   */
  private patchEditEndpointSelect(): void {
    const picker = this.pCalendar;
    if (!picker) return;
    const original = picker.onDateSelect.bind(picker);
    picker.onDateSelect = (event: Event, dateMeta: DayMeta) => {
      if (!this.editingEndpoint) return original(event, dateMeta);
      event.preventDefault();
      if (!dateMeta.selectable || picker.$disabled()) return;
      this.endpointSelected.emit(
        new Date(dateMeta.year, dateMeta.month, dateMeta.day)
      );
    };
  }

  /**
   * Design wants 3-letter weekday labels (Sun, Mon, …), but PrimeNG builds the
   * header from the 2-letter `dayNamesMin` set and `locale` is a no-op in v19.
   * Override `createWeekDays` to rebuild `weekDays` from `dayNamesShort` on this
   * instance only (re-runs correctly when `firstDayOfWeek` changes).
   */
  private patchWeekdayLabels(): void {
    const picker = this.pCalendar;
    if (!picker) return;
    picker.createWeekDays = () => {
      const labels = picker.getTranslation('dayNamesShort') as string[];
      const days: string[] = [];
      let dayIndex = picker.getFirstDateOfWeek();
      for (let i = 0; i < 7; i++) {
        days.push(labels[dayIndex] as string);
        dayIndex = (dayIndex + 1) % 7;
      }
      picker.weekDays = days;
    };
  }

  private isSameDay(date: Date | null | undefined, meta: DayMeta): boolean {
    return (
      !!date &&
      date.getDate() === meta.day &&
      date.getMonth() === meta.month &&
      date.getFullYear() === meta.year
    );
  }

  clearSelections(): void {
    const startElement = this.el.nativeElement.querySelector('.selected-start');
    const endElement = this.el.nativeElement.querySelector('.selected-end');

    if (startElement) {
      this.renderer.removeClass(startElement, 'selected-start');
    }
    if (endElement) {
      this.renderer.removeClass(endElement, 'selected-end');
    }
  }

  handleShow(event: any) {
    if (this.selectionMode === 'range' && this.resolution !== 'week') {
      setTimeout(() => {
        this.applyStartAndEndClasses(this.value);
      }, 100);
    }
    this.onShow.emit(event);
  }

  handleMonthChange(event: any) {
    if (this.selectionMode === 'range' && this.resolution !== 'week') {
      setTimeout(() => {
        this.applyStartAndEndClasses(this.value);
      }, 100);
    }
    this.onMonthChange.emit(event);
  }

  handleKeydown(event: KeyboardEvent): void {
    // Only typed input can carry a "start - end" value; keydowns bubbling up
    // from inline day cells (spans) must not reach handleSelectionRange.
    if (
      event.key === 'Enter' &&
      this.selectionMode === 'range' &&
      event.target instanceof HTMLInputElement
    ) {
      this.handleSelectionRange(event);
    }
  }

  handleSelectionRange(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    const values = value.split(' - ');
    if (values.length === 2) {
      const startDate = new Date(values[0] as string);
      const endDate = new Date(values[1] as string);
      if (
        this.isValid(startDate) &&
        this.isValid(endDate) &&
        this.validateRange(startDate, endDate)
      ) {
        this.value = [startDate, endDate];
        this.onSelect.emit(startDate);
        this.onSelect.emit(endDate);
      }
    }
  }

  isValid(date: Date): boolean {
    return !isNaN(date.getTime());
  }

  validateRange(startDate: Date, endDate: Date): boolean {
    if (this.minDate && startDate < this.minDate) {
      return false;
    }
    if (this.maxDate && endDate > this.maxDate) {
      return false;
    }
    if (startDate > endDate) {
      return false;
    }
    return true;
  }
}
